/**
 * Validation utilities for credential request codes
 */

export interface CodeValidationResult {
  isValid: boolean;
  error?: string;
  formattedCode?: string;
}

/**
 * Validates credential request code format: SOL{YY}-{###}
 * @param code - The code to validate
 * @param year - The year from the credential request
 * @returns Validation result with formatted code if valid
 */
export function validateCredentialRequestCode(
  code: string,
  year: number
): CodeValidationResult {
  if (!code || !code.trim()) {
    return {
      isValid: false,
      error: 'El código no puede estar vacío',
    };
  }

  const trimmedCode = code.trim();
  
  // Pattern: SOL + 2 digits + - + 3 digits (case insensitive)
  const codePattern = /^SOL(\d{2})-(\d{3})$/i;
  const match = trimmedCode.match(codePattern);

  if (!match) {
    return {
      isValid: false,
      error: 'Formato inválido. Debe ser: SOL{YY}-{###} (ej: SOL25-001)',
    };
  }

  const [, yearDigits, number] = match;
  const expectedYearDigits = year.toString().slice(-2);

  // Validate year matches
  if (yearDigits !== expectedYearDigits) {
    return {
      isValid: false,
      error: `El año en el código (${yearDigits}) no coincide con el año de la solicitud (${expectedYearDigits})`,
    };
  }

  // Validate number is not zero
  if (parseInt(number, 10) === 0) {
    return {
      isValid: false,
      error: 'El número de secuencia debe ser mayor a 000',
    };
  }

  // Return formatted code in uppercase
  return {
    isValid: true,
    formattedCode: trimmedCode.toUpperCase(),
  };
}
